import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EmployeeGoal,
  type EmployeeProfile,
} from '../../../shared/api/employee.service';
import { type EmployeeAuthContact } from '../../../shared/hooks/use-employee-session';
import {
  formatCurrency,
  formatGoalProgressLabelByCountry,
  resolveCurrencySymbol,
  resolveEmployeeGoalTargetAmount,
  resolveEmployeeGoalText,
  resolveEmployeeSessionCountryCode,
} from '../../../shared/model';

type RegistrationCompletePreviewVariant = 'with-photo' | 'without-photo';

type RegistrationCompletePreviewContent = {
  donationCurrencySymbol: string;
  donationOptionLabels: ReadonlyArray<string>;
  donationValueLabel: string;
  goalText: string;
  profileNameFallback: string;
  progressPercent: number | null;
  progressLabel: string | null;
};

const DONATION_AMOUNTS = [10, 30, 50, 100, 300];
const DONATION_VALUE = 50;

function resolveProgressCurrentAmount(
  goal: EmployeeGoal | null,
  previewVariant: RegistrationCompletePreviewVariant,
): number {
  const currentAmount = goal?.currentAmount;

  if (
    typeof currentAmount === 'number' &&
    Number.isFinite(currentAmount) &&
    currentAmount >= 0
  ) {
    return currentAmount;
  }

  return previewVariant === 'without-photo' ? 0 : 1000;
}

function calculateProgressPercent(currentAmount: number, targetAmount: number) {
  if (
    !Number.isFinite(currentAmount) ||
    !Number.isFinite(targetAmount) ||
    targetAmount <= 0
  ) {
    return 0;
  }

  const completionPercent = Math.round((currentAmount / targetAmount) * 100);

  return Math.min(100, Math.max(0, completionPercent));
}

export function useRegistrationCompletePreviewContent(
  {
    authContact = null,
    goal = null,
    profile = null,
  }: {
    authContact?: EmployeeAuthContact | null;
    goal?: EmployeeGoal | null;
    profile?: EmployeeProfile | null;
  } = {},
  previewVariant: RegistrationCompletePreviewVariant = 'with-photo',
): RegistrationCompletePreviewContent {
  const { t } = useTranslation();
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const goalTargetAmount = resolveEmployeeGoalTargetAmount(goal);
  const goalText = resolveEmployeeGoalText(
    goal,
    t('registrationComplete.preview.story'),
  );

  return useMemo(() => {
    const currentAmount = resolveProgressCurrentAmount(goal, previewVariant);
    const progressPercent =
      goalTargetAmount === null
        ? null
        : calculateProgressPercent(currentAmount, goalTargetAmount);
    const progressLabel =
      goalTargetAmount === null || progressPercent === null
        ? null
        : formatGoalProgressLabelByCountry(
            currentAmount,
            goalTargetAmount,
            progressPercent,
            countryCode,
          );

    return {
      donationCurrencySymbol: resolveCurrencySymbol(countryCode),
      donationOptionLabels: DONATION_AMOUNTS.map(amount =>
        formatCurrency(amount, countryCode),
      ),
      donationValueLabel: DONATION_VALUE.toString(),
      goalText,
      profileNameFallback: t('registrationComplete.preview.profileName'),
      progressPercent,
      progressLabel,
    };
  }, [countryCode, goal, goalTargetAmount, goalText, previewVariant, t]);
}
